import { useMemo, useState } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Check, ChevronDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchableOption {
  value: string;
  label: string;
  /** Smaller grey text after the label, also searched (an email, a division, a number...). */
  hint?: string | null;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SearchableOption[];
  placeholder?: string;
  /** Adds a first option such as "All documents" that stands for the value `allValue`. */
  allLabel?: string;
  allValue?: string;
  /** Keep the options in the order given instead of A-Z. */
  keepOrder?: boolean;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  id?: string;
  "aria-label"?: string;
  className?: string;
  /** Lets the person use a name that is not in the list: a row "Use "typed text"" appears while typing. */
  onCustom?: (text: string) => void;
  /** The typed name currently in use; shown in the box while nothing is picked from the list. */
  customText?: string;
  /** "sm" matches the compact filter bars (h-8 text-xs); "md" matches form fields. */
  size?: "sm" | "md";
}

/**
 * A drop-down you can type into: the list is alphabetical and narrows as soon as you
 * type a letter. Use it wherever a person picks one thing from a list that can be long
 * (documents, workers, projects, companies, certifications, divisions...).
 */
export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "Select...",
  allLabel,
  allValue = "all",
  keepOrder = false,
  searchPlaceholder = "Type to search...",
  emptyText = "Nothing found.",
  disabled,
  id,
  className,
  size = "md",
  onCustom,
  customText,
  ...rest
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const sorted = useMemo(
    () =>
      keepOrder
        ? options
        : [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true })),
    [options, keepOrder]
  );

  const q = query.trim().toLowerCase();
  const visible = useMemo(() => {
    if (!q) return sorted;
    const hits = sorted.filter((o) => o.label.toLowerCase().includes(q) || (o.hint ?? "").toLowerCase().includes(q));
    // Names that start with what was typed come first, the rest keep A-Z.
    return hits.sort((a, b) => Number(b.label.toLowerCase().startsWith(q)) - Number(a.label.toLowerCase().startsWith(q)));
  }, [sorted, q]);

  const selected = options.find((o) => o.value === value);
  const showAll = !!allLabel && (!q || allLabel.toLowerCase().includes(q));
  const typed = query.trim();
  const canUseTyped = !!onCustom && typed.length > 0 && !options.some((o) => o.label.toLowerCase() === typed.toLowerCase());
  const triggerText = customText && value === allValue ? customText : value === allValue && allLabel ? allLabel : selected?.label;

  const pick = (v: string) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  const row = "flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left hover:bg-muted focus:bg-muted focus:outline-none";

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverPrimitive.Trigger
        id={id}
        disabled={disabled}
        aria-label={rest["aria-label"]}
        role="combobox"
        aria-expanded={open}
        className={cn(
          "flex w-full items-center justify-between rounded-md border border-input bg-background px-3 text-left ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          size === "sm" ? "h-8 text-xs" : "h-10 text-sm",
          className
        )}
      >
        <span className={cn("truncate", !triggerText && "text-muted-foreground")}>{triggerText || placeholder}</span>
        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            "z-[100] w-[var(--radix-popover-trigger-width)] min-w-[14rem] rounded-md border bg-popover text-popover-foreground shadow-md",
            size === "sm" ? "text-xs" : "text-sm"
          )}
          onOpenAutoFocus={(e) => {
            // Focus the search box, not the first row.
            e.preventDefault();
            (e.currentTarget as HTMLElement).querySelector("input")?.focus();
          }}
        >
          <div className="flex items-center border-b px-2">
            <Search className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (visible[0]) pick(visible[0].value);
                  else if (canUseTyped) { onCustom?.(typed); setOpen(false); setQuery(""); }
                  else if (showAll) pick(allValue);
                }
              }}
              placeholder={searchPlaceholder}
              aria-label="Search the list"
              className="h-9 w-full bg-transparent text-base outline-none placeholder:text-muted-foreground md:text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1" role="listbox">
            {canUseTyped && (
              <button type="button" role="option" aria-selected={false} className={row} onClick={() => { onCustom?.(typed); setOpen(false); setQuery(""); }}>
                <Check className="h-3.5 w-3.5 shrink-0 opacity-0" />
                <span className="min-w-0 flex-1 truncate font-medium">Use &ldquo;{typed}&rdquo;</span>
              </button>
            )}
            {showAll && (
              <button type="button" role="option" aria-selected={value === allValue} className={row} onClick={() => pick(allValue)}>
                <Check className={cn("h-3.5 w-3.5 shrink-0", value === allValue ? "opacity-100" : "opacity-0")} />
                <span className="truncate font-medium">{allLabel}</span>
              </button>
            )}
            {visible.map((o) => (
              <button key={o.value} type="button" role="option" aria-selected={value === o.value} className={row} onClick={() => pick(o.value)}>
                <Check className={cn("h-3.5 w-3.5 shrink-0", value === o.value ? "opacity-100" : "opacity-0")} />
                <span className="min-w-0 flex-1 truncate">
                  {o.label}
                  {o.hint ? <span className="ml-2 text-muted-foreground">{o.hint}</span> : null}
                </span>
              </button>
            ))}
            {visible.length === 0 && !showAll && !canUseTyped && <p className="px-2 py-4 text-center text-muted-foreground">{emptyText}</p>}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
